import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  Calendar, 
  ChevronRight, 
  Shield, 
  ShieldCheck, 
  Eye, 
  MessageSquare,
  Building 
} from 'lucide-react';
import { getSharedPrompts } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const SharedPrompts = ({ user }) => {
  const [sharedPrompts, setSharedPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSharedPrompts();
  }, [user]);

  const loadSharedPrompts = async () => {
    if (!user) return;
    
    try {
      const promptsData = await getSharedPrompts(user.id);
      setSharedPrompts(promptsData);
    } catch (error) {
      console.error('Failed to load shared prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPermissionIcon = (level) => {
    switch (level) {
      case 'owner':
        return <ShieldCheck className="h-4 w-4 text-celadon-600" />;
      case 'editor':
        return <Shield className="h-4 w-4 text-celadon-500" />;
      case 'reviewer':
        return <Eye className="h-4 w-4 text-gray-600" />;
      case 'commenter':
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
      default:
        return <Users className="h-4 w-4 text-gray-400" />;
    }
  };

  const getPermissionColor = (level) => {
    switch (level) {
      case 'owner':
        return 'bg-celadon-100 text-celadon-800';
      case 'editor':
        return 'bg-celadon-50 text-celadon-700';
      case 'reviewer':
        return 'bg-gray-100 text-gray-700';
      case 'commenter':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-celadon-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Shared Prompts</h1>
          <p className="text-gray-600 mt-1">Prompts where you're collaborating with others</p>
        </div>
      </div>

      {/* Shared Prompts List */}
      {sharedPrompts.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No shared prompts yet</h3>
          <p className="text-gray-600 mb-4">
            When others invite you to collaborate on prompts, they'll appear here
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sharedPrompts.map((prompt) => (
            <Link
              key={prompt.id}
              to={`/prompts/${prompt.id}`}
              className="card p-6 hover:shadow-lg transition-shadow duration-200 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-1">
                    {prompt.name}
                  </h3>
                  {prompt.description && (
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {prompt.description}
                    </p>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
              </div>
              
              {/* Project info */}
              <div className="flex items-center text-sm text-gray-600 mb-3">
                <Building className="h-4 w-4 mr-1" />
                <span className="truncate">
                  {prompt.project_name} • by {prompt.project_owner_name}
                </span>
              </div>

              {/* Permission badge */}
              <div className="flex items-center mb-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPermissionColor(prompt.permission_level)}`}>
                  {getPermissionIcon(prompt.permission_level)}
                  <span className="ml-1 capitalize">{prompt.permission_level}</span>
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-500">
                  <FileText className="h-4 w-4 mr-1" />
                  {prompt.version_count} version{prompt.version_count !== '1' ? 's' : ''}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="h-4 w-4 mr-1" />
                  {formatDistanceToNow(new Date(prompt.collaboration_started), { addSuffix: true })}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default SharedPrompts; 